<script setup lang="ts" generic="ColumnId extends string">
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import ButtonIcon from '#components/elements/button/buttonIcon/ButtonIcon.vue';
import type { CheckBoxeType } from '#components/elements/checkBox/CheckBoxGroup.vue';
import { useLanguage } from '#composables/useLanguage';
import type { DataTableColumn, DataTableKpiDisplayNameType } from '@carrefour-gcs/shared';
import { ref, watch } from 'vue';
import { kpisToCheckboxList } from '../configurationPopIn.helpers';
import SortableCheckBoxList from './SortableCheckBoxList.vue';

const { availableKpis, selectedKpis, columnsOrder, isWithTax } = defineProps<{
  availableKpis: Pick<DataTableColumn, 'name' | 'label' | 'required'>[];
  selectedKpis: DataTableKpiDisplayNameType[];
  columnsOrder: ColumnId[];
  isWithTax: boolean;
}>();

const { t } = useLanguage();

const isKpisSectionExpanded = ref(true);

const formKpisList = defineModel<CheckBoxeType[]>('formKpisList', { default: () => [] });

function selectAllKpis() {
  formKpisList.value = kpisToCheckboxList(
    availableKpis,
    selectedKpis,
    columnsOrder,
    isWithTax,
    t,
    formKpisList.value,
    true
  );
}

function unselectAllKpis() {
  formKpisList.value = kpisToCheckboxList(
    availableKpis,
    selectedKpis,
    columnsOrder,
    isWithTax,
    t,
    formKpisList.value,
    false
  );
}

function onKpisSelectionChanged(list: CheckBoxeType[]) {
  formKpisList.value = list;
}

function orderKpis(list: CheckBoxeType[]) {
  formKpisList.value = list;
}

watch(
  () => availableKpis,
  () => {
    formKpisList.value = kpisToCheckboxList<ColumnId>(
      availableKpis,
      selectedKpis,
      columnsOrder,
      isWithTax,
      t,
      formKpisList.value
    );
  },
  { immediate: true }
);
</script>

<template>
  <div class="my-4">
    <div class="flex items-center justify-between">
      <h4 class="text-xl text-primary font-bold">
        {{ t('dataTableConfigurationPopIn.kpis.title') }}
      </h4>

      <ButtonIcon
        variant="primary"
        :icon="isKpisSectionExpanded ? 'chevron-up' : 'chevron-down'"
        background="transparent"
        @click="isKpisSectionExpanded = !isKpisSectionExpanded"
      />
    </div>

    <div v-if="isKpisSectionExpanded" class="mt-2">
      <p>{{ t('dataTableConfigurationPopIn.kpis.subtitle') }}</p>

      <div class="flex justify-end mb-1 pr-4 gap-4">
        <ButtonBase
          class="font-bold"
          :label="t('dataTableConfigurationPopIn.selectAll')"
          variant="secondary"
          background="transparent"
          @click="selectAllKpis"
        />
        <ButtonBase
          class="font-bold"
          :label="t('dataTableConfigurationPopIn.unselectAll')"
          variant="secondary"
          background="transparent"
          @click="unselectAllKpis"
        />
      </div>
      <div class="max-h-[378px] py-1 mr-4 overflow-y-auto custom-vertical-scrollbar pr-4">
        <SortableCheckBoxList
          v-model="formKpisList"
          @update:model-value="onKpisSelectionChanged"
          @update-list="orderKpis"
        />
      </div>
    </div>
  </div>
</template>
